// API-backed store with React event-based reactivity

export interface ContentItem {
  id: string;
  title: string;
  body: string;
  status: "ideas" | "draft" | "review" | "ready" | "published";
  platforms: string[];
  source: string;
  createdBy: string;
  createdAt: string;
  order: number;
}

export interface TaskItem {
  id: string;
  title: string;
  description: string;
  status: "todo" | "inprogress" | "done";
  assignedTo: string;
  priority: "low" | "med" | "high";
  dueDate?: string;
  createdBy: string;
  createdAt: string;
  order: number;
}

export type LeadStatus = "new" | "contacted" | "followup" | "responded" | "meeting_booked" | "proposal_sent" | "hold" | "won" | "lost";

export interface LeadItem {
  id: number;
  name: string;
  title: string;
  company: string;
  website: string;
  linkedin: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  status: LeadStatus;
  notes: string;
  lastContacted: string;
  createdAt: string;
  updatedAt: string;
}

export interface LeadNote {
  id: number;
  leadId: number;
  text: string;
  createdAt: string;
}

function notify(key: string) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("store-update", { detail: key }));
  }
}

export const contentStore = {
  list: async (): Promise<ContentItem[]> => {
    const res = await fetch("/api/content");
    return res.json();
  },
  create: async (item: Omit<ContentItem, "id" | "createdAt" | "order">) => {
    const res = await fetch("/api/content", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(item) });
    notify("mc-content");
    return res.json();
  },
  update: async (id: string, fields: Partial<ContentItem>) => {
    const res = await fetch(`/api/content/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(fields) });
    notify("mc-content");
    return res.json();
  },
  remove: async (id: string) => {
    await fetch(`/api/content/${id}`, { method: "DELETE" });
    notify("mc-content");
  },
};

export const taskStore = {
  list: async (): Promise<TaskItem[]> => {
    const res = await fetch("/api/tasks");
    return res.json();
  },
  create: async (item: Omit<TaskItem, "id" | "createdAt" | "order">) => {
    const res = await fetch("/api/tasks", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(item) });
    notify("mc-tasks");
    return res.json();
  },
  update: async (id: string, fields: Partial<TaskItem>) => {
    const res = await fetch(`/api/tasks/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(fields) });
    notify("mc-tasks");
    return res.json();
  },
  remove: async (id: string) => {
    await fetch(`/api/tasks/${id}`, { method: "DELETE" });
    notify("mc-tasks");
  },
};

export const leadStore = {
  list: async (params?: { status?: string; search?: string }): Promise<LeadItem[]> => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.search) qs.set("search", params.search);
    const query = qs.toString();
    const res = await fetch(`/api/leads${query ? `?${query}` : ""}`);
    return res.json();
  },
  create: async (item: Partial<LeadItem>) => {
    const res = await fetch("/api/leads", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(item) });
    notify("mc-leads");
    return res.json();
  },
  update: async (id: number, fields: Partial<LeadItem>) => {
    const res = await fetch(`/api/leads/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(fields) });
    notify("mc-leads");
    return res.json();
  },
  remove: async (id: number) => {
    await fetch(`/api/leads/${id}`, { method: "DELETE" });
    notify("mc-leads");
  },
  notes: async (id: number): Promise<LeadNote[]> => {
    const res = await fetch(`/api/leads/${id}/notes`);
    return res.json();
  },
  addNote: async (id: number, text: string) => {
    const res = await fetch(`/api/leads/${id}/notes`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }) });
    notify("mc-leads");
    return res.json();
  },
};
